import { httpClient } from '@/core/api/httpClient';
import { toPascalCaseParams } from '@/shared/lib/to-pascal-case-params';
import type { Tag, TagFilters, TagPagedResult } from '../types';
import type { TagFormValues } from '../schemas';
import { mockTags } from './tags.mock';
import { env } from '@/core/config/env';
import { API_V1 } from '@/core/api/endpoints';

const USE_MOCK = env.VITE_USE_MOCK === 'true';

const tagsMock: Tag[] = [...mockTags];

export const tagsService = {
  getList: async (
    projectId: string,
    filters?: TagFilters,
    signal?: AbortSignal,
  ): Promise<TagPagedResult> => {
    if (USE_MOCK) {
      let items = tagsMock.filter((t) => t.projectId === projectId);

      if (filters?.searchTerm) {
        const term = filters.searchTerm.toLowerCase();
        items = items.filter((t) => t.name?.toLowerCase().includes(term));
      }

      const page = filters?.page ?? 1;
      const pageSize = filters?.pageSize ?? 20;
      const totalCount = items.length;
      const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
      const start = (page - 1) * pageSize;
      const pageItems = items.slice(start, start + pageSize);

      return {
        items: pageItems,
        page,
        pageSize,
        totalCount,
        totalPages,
        hasPreviousPage: page > 1,
        hasNextPage: page < totalPages,
        firstItemOnPage: totalCount === 0 ? 0 : start + 1,
        lastItemOnPage: Math.min(start + pageSize, totalCount),
      };
    }

    const { data } = await httpClient.get<TagPagedResult>(`${API_V1}/projects/${projectId}/tags`, {
      params: filters ? toPascalCaseParams(filters as Record<string, unknown>) : undefined,
      signal,
    });
    return data;
  },

  create: async (projectId: string, payload: TagFormValues): Promise<void> => {
    if (USE_MOCK) {
      const newTag: Tag = {
        id: `tag-${Date.now()}`,
        projectId,
        name: payload.name,
        color: payload.color,
        createdAt: new Date().toISOString(),
      };
      tagsMock.push(newTag);
      return;
    }
    await httpClient.post<TagFormValues, void>(`${API_V1}/projects/${projectId}/tags`, payload);
  },

  update: async (projectId: string, tagId: string, payload: TagFormValues): Promise<void> => {
    if (USE_MOCK) {
      const idx = tagsMock.findIndex((t) => t.id === tagId && t.projectId === projectId);
      if (idx !== -1) tagsMock[idx] = { ...tagsMock[idx], ...payload };
      return;
    }
    await httpClient.put<TagFormValues, void>(
      `${API_V1}/projects/${projectId}/tags/${tagId}`,
      payload,
    );
  },

  delete: async (projectId: string, tagId: string): Promise<void> => {
    if (USE_MOCK) {
      const idx = tagsMock.findIndex((t) => t.id === tagId && t.projectId === projectId);
      if (idx !== -1) tagsMock.splice(idx, 1);
      return;
    }
    await httpClient.delete<void>(`${API_V1}/projects/${projectId}/tags/${tagId}`);
  },
};
